import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import PublicIcon from "@mui/icons-material/Public";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MenuIpCopySnackbar from "./MenuIpCopySnackbar";
import { Link as RouterLink } from "react-router-dom";

const pages = [
  { name: "Home", url: "/" },
  { name: "Rules", url: "/rules" },
  {
    name: "Guide",
    items: [
      { name: "26k SMP", url: "/guide" },
      { name: "Hardcore", url: "/guide-hardcore" },
    ],
  },
  {
    name: "Stats",
    items: [
      { name: "26k Monthly Stats", url: "/monthly-stats" },
      { name: "Market Stats", isSection: true, url: "/markets" },
      { name: "Historical Weekly", url: "/markets?tab=historical-weekly" },
      { name: "Historical Monthly", url: "/markets?tab=historical-monthly" },
      { name: "Leaderboard", url: "/markets?tab=leaderboard" },
      { name: "Stock Report", url: "/markets?tab=stock-report" },
      { name: "Money Supply", url: "/markets?tab=money-supply" },
      { name: "Villager Shops", url: "/markets?tab=villager-shops" },
      { name: "Player Stats", isSection: true },
      { name: "26k SMP", url: "/stats-smp" },
      { name: "26k SMP 2 Year", url: "/stats-two-year" },
      { name: "Hardcore", url: "/stats-hardcore" },
    ],
  },
  { name: "SMP Map", url: "/map" },
  {
    name: "Discord ⤴",
    url: "https://discord.gg/4z6bf9sy44",
    external: true,
  },
  {
    name: "Store ⤴",
    url: "https://mcverse-store.tebex.io/",
    external: true,
  },
  { name: "Vote", url: "/vote", icon: "vote" },
];

function ResponsiveAppBar() {
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  // Generic anchor map for desktop dropdowns: { [pageName]: DOMElement | null }
  const [anchorEls, setAnchorEls] = React.useState({});
  // Mobile popout submenu state
  const [mobileSubmenuAnchor, setMobileSubmenuAnchor] = React.useState(null);
  const [mobileSubmenuPage, setMobileSubmenuPage] = React.useState(null);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenMenu = (name) => (event) => {
    setAnchorEls((prev) => ({ ...prev, [name]: event.currentTarget }));
  };

  const handleCloseMenu = (name) => () => {
    setAnchorEls((prev) => ({ ...prev, [name]: null }));
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
    setMobileSubmenuAnchor(null);
    setMobileSubmenuPage(null);
  };

  const handleOpenMobileSubmenu = (page) => (event) => {
    setMobileSubmenuAnchor(event.currentTarget);
    setMobileSubmenuPage(page);
  };

  const handleCloseMobileSubmenu = () => {
    setMobileSubmenuAnchor(null);
    setMobileSubmenuPage(null);
  };

  // Render section header items — optionally clickable
  const renderSectionHeader = (item, closeHandler, extraSx = {}) => {
    const base = {
      py: 0.75,
      fontSize: "0.72rem",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      minHeight: "unset",
      ...extraSx,
    };

    if (item.url) {
      return (
        <MenuItem
          key={`section-${item.name}`}
          component={RouterLink}
          to={item.url}
          onClick={closeHandler}
          sx={{ ...base, color: "primary.main", "&:hover": { bgcolor: "action.hover" } }}
        >
          {item.name}
        </MenuItem>
      );
    }

    return (
      <MenuItem
        key={`section-${item.name}`}
        disabled
        sx={{ ...base, color: "text.secondary", opacity: "1 !important" }}
      >
        {item.name}
      </MenuItem>
    );
  };

  return (
    <AppBar position="static" color="header">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Desktop logo */}
          <PublicIcon
            sx={{ display: { xs: "none", lg: "flex" }, mr: 1, color: "#fff" }}
          />
          <Typography
            variant="h6"
            noWrap
            component={RouterLink}
            to="/"
            sx={{
              mr: 2,
              display: { xs: "none", lg: "flex" },
              fontFamily: "monospace",
              fontWeight: 700,
              letterSpacing: ".1rem",
              color: "#fff",
              textDecoration: "none",
            }}
          >
            MCVerse
          </Typography>

          {/* Mobile hamburger */}
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: "flex", lg: "none" },
              color: "#ffffff",
            }}
          >
            <IconButton
              size="large"
              aria-label="navigation menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>

            {/* Primary mobile menu */}
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              keepMounted
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{ display: { xs: "block", lg: "none" } }}
            >
              {pages.map((page) => {
                if (page.items) {
                  return (
                    <MenuItem
                      key={page.name}
                      onClick={handleOpenMobileSubmenu(page)}
                      sx={{ display: "flex", justifyContent: "space-between", fontWeight: "bold" }}
                    >
                      <Typography textAlign="left">{page.name}</Typography>
                      <ChevronRightIcon fontSize="small" sx={{ ml: 1 }} />
                    </MenuItem>
                  );
                }

                if (page.external) {
                  return (
                    <a
                      href={page.url}
                      key={page.name}
                      style={{ textDecoration: "none", color: "inherit" }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <MenuItem onClick={handleCloseNavMenu}>
                        <Typography textAlign="center">{page.name}</Typography>
                      </MenuItem>
                    </a>
                  );
                }

                return (
                  <RouterLink
                    to={page.url}
                    key={page.name}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <MenuItem onClick={handleCloseNavMenu}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        {page.icon === "vote" && <HowToVoteIcon fontSize="small" />}
                        <Typography textAlign="center">{page.name}</Typography>
                      </Box>
                    </MenuItem>
                  </RouterLink>
                );
              })}
            </Menu>

            {/* Mobile popout submenu — appears to the right of the tapped item */}
            <Menu
              anchorEl={mobileSubmenuAnchor}
              open={Boolean(mobileSubmenuAnchor)}
              onClose={handleCloseMobileSubmenu}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              PaperProps={{ sx: { minWidth: 200 } }}
              sx={{ display: { xs: "block", lg: "none" } }}
            >
              {mobileSubmenuPage?.items.flatMap((item) => {
                if (item.isSection) {
                  return [
                    <Divider key={`divider-${item.name}`} />,
                    renderSectionHeader(item, handleCloseNavMenu, { px: 2 }),
                  ];
                }
                return [
                  <MenuItem
                    key={`${mobileSubmenuPage.name}-${item.name}`}
                    component={RouterLink}
                    to={item.url}
                    onClick={handleCloseNavMenu}
                    sx={{ pl: 3 }}
                  >
                    <Typography textAlign="left">{item.name}</Typography>
                  </MenuItem>,
                ];
              })}
            </Menu>
          </Box>

          {/* Mobile logo */}
          <PublicIcon
            sx={{ display: { xs: "flex", lg: "none" }, mr: 1, color: "#ffffff" }}
          />
          <Typography
            variant="h5"
            noWrap
            component={RouterLink}
            to="/"
            sx={{
              mr: 2,
              display: { xs: "flex", lg: "none" },
              flexGrow: 1,
              fontFamily: "monospace",
              fontWeight: 400,
              letterSpacing: ".1rem",
              color: "#ffffff",
              textDecoration: "none",
            }}
          >
            MCVerse
          </Typography>

          {/* Desktop nav */}
          <Box sx={{ flexGrow: 1, display: { xs: "none", lg: "flex" } }}>
            {pages.flatMap((page) => {
              if (page.items) {
                const isOpen = Boolean(anchorEls[page.name]);
                return [
                  <Button
                    key={`${page.name}-button`}
                    onClick={handleOpenMenu(page.name)}
                    endIcon={
                      isOpen ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )
                    }
                    sx={{ my: 2, color: "white", display: "flex", px: 1 }}
                  >
                    {page.name}
                  </Button>,
                  <Menu
                    key={`${page.name}-menu`}
                    anchorEl={anchorEls[page.name]}
                    open={isOpen}
                    onClose={handleCloseMenu(page.name)}
                    PaperProps={{ sx: { minWidth: 200 } }}
                  >
                    {page.items.flatMap((item) => {
                      if (item.isSection) {
                        return [
                          <Divider key={`divider-${item.name}`} />,
                          renderSectionHeader(item, handleCloseMenu(page.name), { px: 2.5 }),
                        ];
                      }
                      return [
                        <MenuItem
                          key={item.name}
                          component={RouterLink}
                          to={item.url}
                          onClick={handleCloseMenu(page.name)}
                          sx={{ py: 1.5, px: 2.5 }}
                        >
                          {item.name}
                        </MenuItem>,
                      ];
                    })}
                  </Menu>,
                ];
              }

              if (page.external) {
                return (
                  <Button
                    key={page.name}
                    href={page.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ my: 2, px: 2, color: "white", display: "block" }}
                  >
                    {page.name}
                  </Button>
                );
              }

              return (
                <Button
                  key={page.name}
                  component={RouterLink}
                  to={page.url}
                  startIcon={
                    page.icon === "vote" ? <HowToVoteIcon fontSize="small" /> : undefined
                  }
                  sx={{ my: 2, px: 2, color: "white", display: "flex" }}
                >
                  {page.name}
                </Button>
              );
            })}
          </Box>

          <Box sx={{ flexGrow: 0 }}>
            <MenuIpCopySnackbar />
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default ResponsiveAppBar;
